import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import color from "../../../Constants/colors";
import { formatDate } from "../../../Helpers/dateHelpers";
import { getFileIcon } from "../../../Helpers/iconHelpers";
import {
  statusToStringFile,
  truncateString,
} from "../../../Helpers/stringHelpers";
import { getStatusTextColor } from "../../../Helpers/evaluationHelpers";
import { useTranslation } from "react-i18next";

interface UploadedFilesPanelProps<T> {
  file: T;
  getFileName: (file: T) => string;
  getFileCreated: (file: T) => string | undefined;
  getFileStatus: (file: T) => number;
  getFileUpdated: (file: T) => string | undefined;
}

const TempUploadedFilesPanel = <T,>({
  file,
  getFileName,
  getFileStatus,
  getFileCreated,
  getFileUpdated,
}: UploadedFilesPanelProps<T>) => {
  const fileName = getFileName(file);
  const fileStatus = getFileStatus(file);
  const fileCreated = formatDate(getFileCreated(file));
  const fileUpdated = formatDate(getFileUpdated(file));
  const { t } = useTranslation("modal");

  return (
    <Tooltip
      title={
        <>
          <div>
            {t("created_at")} {fileCreated}
          </div>
          <div>
            {t("updated_at")} {fileUpdated}
          </div>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          cursor: "pointer",
          padding: "2px 4px",
          borderRadius: "4px",
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: "rgba(242, 141, 1, 0.08)",
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& svg": {
              width: 40,
              height: 40,
            },
            "& img": {
              width: 40,
              height: 40,
              objectFit: "contain",
            },
          }}
        >
          {getFileIcon(fileName)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: color.accent,
              fontWeight: 700,
              fontSize: "0.75rem",
              lineHeight: 1.1,
              overflow: "hidden",
              whiteSpace: "nowrap",
              display: "block",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            title={fileName}
          >
            {fileName}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: getStatusTextColor(fileStatus),
              fontWeight: 700,
              fontSize: "0.7rem",
              lineHeight: 1,
            }}
          >
            {statusToStringFile(fileStatus)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TempUploadedFilesPanel;
